import { Dispatch, SetStateAction } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { FormikHelpers } from 'formik';
import { enqueueSnackbar } from 'notistack';

import { useShowError } from 'hooks/useShowError';
import { useFormMutation } from 'mutations/useFormMutation';
import { useDreConfigContext } from 'pages/financeiro/dre/configuracao/contexts/DreConfigContext';
import { DreConfiguracao } from 'pages/financeiro/dre/configuracao/types';

export function useMutateConfiguracao({ setOpen }: { setOpen: Dispatch<SetStateAction<boolean>> }) {
    const { activeGrupo, activeSubgrupo } = useDreConfigContext();
    const isVendas = activeGrupo?.dreMercadologicaTipoGrupoExibicaoEnum === 'VENDAS';
    const { mutate, isLoading } = useFormMutation<AxiosResponse<DreConfiguracao>>();
    const { showError } = useShowError();
    const queryClient = useQueryClient();

    /**
     * Verifica se todas as estruturas estão vazias.
     */
    const allEstruturasEmpty = (estruturas: DreConfiguracao['dreMercadologicaConfiguracaoCategorizacao']) =>
        estruturas.every((estrutura) => !estrutura.divisao && !estrutura.secao && !estrutura.grupo && !estrutura.subgrupo);

    /**
     * Salva a configuração.
     */
    const onSubmitForm = (formData: DreConfiguracao, formik: FormikHelpers<DreConfiguracao>) => {
        const isCreating = !formData.idDreMercadologicaConfiguracao;

        if (isVendas && allEstruturasEmpty(formData.dreMercadologicaConfiguracaoCategorizacao)) {
            enqueueSnackbar('Seção ou divisão são obrigatórios dentro da estrutura mercadológica', { variant: 'error' });
            return;
        }

        mutate(
            {
                formik,
                formData,
                url: `/dre/dremercadologicaconfiguracao/insertorupdate?idDreMercadologicaTipoGrupoExibicao=${activeGrupo?.idDreMercadologicaTipoGrupoExibicao}`,
                preventSnack: true,
                preventRedirect: true,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries([
                        'queryConfiguracoes',
                        activeGrupo?.idDreMercadologicaTipoGrupoExibicao,
                        activeSubgrupo?.idDreMercadologicaGrupoExibicao,
                    ]);

                    enqueueSnackbar(`Configuração ${isCreating ? 'criada' : 'atualizada'} com sucesso`, { variant: 'success' });

                    setOpen(false);
                },
                onError: (error) => showError(error, `Falha ao ${isCreating ? 'criar' : 'atualizar'} configuração`),
            },
        );
    };

    return { onSubmitForm, isLoading };
}
